import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Eye, LayoutDashboard, CheckCircle,
  Home, Layers, Zap, PanelLeft, PanelRight, ChevronRight,
} from 'lucide-react';
import { dashboardsApi, createDashboard, createWidget, uid } from '../api/dashboardsApi';
import { invalidateQueries } from '../../analytics-shared/hooks/queryShim';
import { deriveWidgetConfig } from '../../analytics-shared/engine/widgetSchema';
import { metadataApi } from '../../analytics-shared/api/metadataApi';
import ReportPickerPanel from '../components/settings/ReportPickerPanel';
import DashboardCanvas from '../components/canvas/DashboardCanvas';
import WidgetSettingsPanel from '../components/settings/WidgetSettingsPanel';

const DEFAULT_WIDGET_W = 6;
const DEFAULT_WIDGET_H = 4;

const DashboardBuilderPage = () => {
  const { dashboardId } = useParams();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState(null);
  const [selectedWidgetId, setSelectedWidgetId] = useState(null);
  const [saveState, setSaveState] = useState('idle');
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let initial = null;
      if (dashboardId && dashboardId !== 'new') {
        initial = await dashboardsApi.get(dashboardId);
      }
      if (!initial) initial = createDashboard();
      if (!cancelled) setDashboard(initial);
      // Stamp Recent on every successful builder entry — matches what
      // the viewer page already does so the Recent sidebar treats
      // builder visits the same way it treats viewer visits.
      if (initial?.id && dashboardId && dashboardId !== 'new') {
        dashboardsApi.markViewed(initial.id).catch(() => {});
      }
    })();
    return () => { cancelled = true; };
  }, [dashboardId]);

  if (!dashboard) {
    return <div className="h-full flex items-center justify-center bg-slate-50 text-slate-400 text-sm">Loading dashboard…</div>;
  }

  const selectedWidget = dashboard.widgets.find((w) => w.id === selectedWidgetId) || null;

  const handleAddReport = async (report) => {
    // Auto-derive a Salesforce-style default config from the report:
    //  - if the report has an embedded chart, mirror it (legacy path)
    //  - else use its first groupBy + first summary as (X, Y, aggregate)
    //  - else pick the first categorical + numeric field on the object
    // The object metadata fetch is best-effort; failure just means we
    // skip the schema-based fallback and start from a blank chart config.
    let derived = {};
    try {
      const objectMeta = await metadataApi.getObject(report.object);
      derived = deriveWidgetConfig(report, objectMeta?.fields || []);
    } catch (e) {
      derived = deriveWidgetConfig(report, []);
    }

    const chartDef = report.charts?.[0];
    const maxY = dashboard.widgets.reduce(
      (max, w) => Math.max(max, (w.layout?.y ?? 0) + (w.layout?.h ?? DEFAULT_WIDGET_H)),
      0,
    );
    const widget = createWidget(report.id, {
      title: report.name,
      subtitle: chartDef?.title || '',
      chartType: derived.chartType || chartDef?.type || 'bar',
      layout: { x: 0, y: maxY, w: DEFAULT_WIDGET_W, h: DEFAULT_WIDGET_H, minW: 2, minH: 2 },
      config: {
        xField: derived.xField || null,
        yField: derived.yField || null,
        aggregate: derived.aggregate || 'COUNT',
      },
    });
    widget.layout.i = widget.id;
    setDashboard((prev) => ({ ...prev, widgets: [...prev.widgets, widget] }));
    setSelectedWidgetId(widget.id);
    setRightOpen(true);
  };

  const handleLayoutChange = (newLayout) => {
    setDashboard((prev) => ({
      ...prev,
      widgets: prev.widgets.map((w) => {
        const l = newLayout.find((item) => item.i === w.id);
        return l ? { ...w, layout: { ...w.layout, ...l, i: w.id } } : w;
      }),
    }));
  };

  const handleWidgetUpdate = (updated) => {
    setDashboard((prev) => ({
      ...prev,
      widgets: prev.widgets.map((w) => (w.id === updated.id ? updated : w)),
    }));
  };

  const handleWidgetRemove = (widgetId) => {
    setDashboard((prev) => ({ ...prev, widgets: prev.widgets.filter((w) => w.id !== widgetId) }));
    setSelectedWidgetId((prev) => (prev === widgetId ? null : prev));
  };

  const handleWidgetDuplicate = (widget) => {
    const newId = uid('wid');
    const clone = {
      ...JSON.parse(JSON.stringify(widget)),
      id: newId,
      title: `${widget.title} (Copy)`,
      layout: { ...widget.layout, i: newId, y: (widget.layout?.y ?? 0) + (widget.layout?.h ?? DEFAULT_WIDGET_H) },
    };
    setDashboard((prev) => ({ ...prev, widgets: [...prev.widgets, clone] }));
    setSelectedWidgetId(newId);
  };

  // Plain handler — was previously useCallback, but it sat AFTER the
  // `if (!dashboard) return …` early return. React saw a different hook
  // count between the initial loading render and the post-load render
  // ("Rendered more hooks than during the previous render"). Hooks-rule
  // fix: move it out of the hook list entirely.
  const handleWidgetConfigure = (widget) => {
    setSelectedWidgetId(widget.id);
    setRightOpen(true);
  };

  const handleSave = async () => {
    setSaveState('saving');
    const saved = await dashboardsApi.save(dashboard);
    setDashboard(saved);
    // Refresh the list cache so name/visibility/widget-count changes show
    // on the dashboards list without a manual reload.
    invalidateQueries(['dashboards']);
    setSaveState('saved');
    setTimeout(() => setSaveState('idle'), 2500);
  };

  return (
    <div className="flex flex-col h-screen bg-[#f4f5f8] overflow-hidden">
      <div className="flex-shrink-0 flex items-center gap-2 px-4 h-12 bg-white border-b border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.06)] z-30">
        <button onClick={() => navigate('/crm-platform')}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-indigo-600 hover:bg-slate-50 px-2 py-1 rounded-lg transition-colors flex-shrink-0"
          title="Back to CRM">
          <Home className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">CRM</span>
        </button>
        <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
        <button onClick={() => navigate('/reports/dashboards')}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 transition-colors group flex-shrink-0">
          <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
          <span className="hidden sm:inline">Dashboards</span>
        </button>
        <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />

        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <div className="w-6 h-6 rounded-md bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <LayoutDashboard className="w-3.5 h-3.5 text-indigo-600" />
          </div>
          {/* Editable title. The rename affordance is a subtle hover/focus
              background on the input itself — we removed the standalone
              decorative pencil icon, which rendered detached in the middle
              of the header because the input is width-capped. */}
          <input value={dashboard.name}
            onChange={(e) => setDashboard((prev) => ({ ...prev, name: e.target.value }))}
            title="Rename dashboard"
            className="text-sm font-semibold text-slate-800 bg-transparent border-0 rounded px-1 -ml-1 focus:outline-none hover:bg-slate-50 focus:bg-slate-50 min-w-0 flex-1 max-w-xs transition-colors"
            placeholder="Dashboard name…" />
        </div>

        <div className="hidden md:flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 flex-shrink-0">
          <Zap className="w-2.5 h-2.5" />Edit Mode
        </div>

        <div className="hidden lg:flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] text-slate-500 bg-slate-100 flex-shrink-0">
          <Layers className="w-3 h-3" />
          {dashboard.widgets.length} widget{dashboard.widgets.length !== 1 ? 's' : ''}
        </div>

        <div className="w-px h-5 bg-slate-100 flex-shrink-0 mx-1" />

        <button onClick={() => navigate(`/reports/dashboards/${dashboard.id}`)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all flex-shrink-0">
          <Eye className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Preview</span>
        </button>

        <button onClick={handleSave} disabled={saveState === 'saving'}
          className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg transition-all shadow-sm flex-shrink-0 ${
            saveState === 'saved'
              ? 'bg-emerald-500 text-white shadow-[0_2px_8px_rgba(16,185,129,0.25)]'
              : 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-700 hover:to-violet-700 shadow-[0_2px_8px_rgba(99,102,241,0.25)]'
          }`}>
          {saveState === 'saved' ? (
            <><CheckCircle className="w-3.5 h-3.5" /><span>Saved!</span></>
          ) : (
            <><Save className="w-3.5 h-3.5" /><span>Save Dashboard</span></>
          )}
        </button>
      </div>

      <div className="flex-shrink-0 flex items-center justify-between px-3 py-1.5 bg-white/60 border-b border-slate-200/80 backdrop-blur-sm">
        <button onClick={() => setLeftOpen((o) => !o)}
          className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-all ${
            leftOpen ? 'bg-white text-indigo-600 border border-indigo-100 shadow-sm' : 'text-slate-500 hover:bg-white/80 hover:text-slate-700 hover:shadow-sm'
          }`}>
          <PanelLeft className="w-3.5 h-3.5" />
          <span className="hidden md:inline">Reports</span>
        </button>

        <p className="text-[11px] text-slate-400 hidden sm:block select-none">Drag to move · Drag corners to resize</p>

        <button onClick={() => selectedWidget && setRightOpen((o) => !o)} disabled={!selectedWidget}
          className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-all ${
            !selectedWidget
              ? 'text-slate-300 cursor-not-allowed'
              : rightOpen
                ? 'bg-white text-indigo-600 border border-indigo-100 shadow-sm'
                : 'text-slate-500 hover:bg-white/80 hover:text-slate-700 hover:shadow-sm'
          }`}>
          <span className="hidden md:inline">Settings</span>
          <PanelRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className={`flex-shrink-0 transition-all duration-200 overflow-hidden ${leftOpen ? 'w-64' : 'w-0'}`}>
          <ReportPickerPanel onAddReport={handleAddReport} />
        </div>

        <div className="flex-1 overflow-hidden">
          <DashboardCanvas
            widgets={dashboard.widgets}
            editMode
            selectedWidgetId={selectedWidgetId}
            onLayoutChange={handleLayoutChange}
            onWidgetRemove={handleWidgetRemove}
            onWidgetConfigure={handleWidgetConfigure}
            onWidgetDuplicate={handleWidgetDuplicate}
            onWidgetSelect={setSelectedWidgetId}
          />
        </div>

        <div className={`flex-shrink-0 transition-all duration-200 overflow-hidden ${rightOpen && selectedWidget ? 'w-64' : 'w-0'}`}>
          <WidgetSettingsPanel widget={selectedWidget} onUpdate={handleWidgetUpdate} onClose={() => setSelectedWidgetId(null)} />
        </div>
      </div>
    </div>
  );
};

export default DashboardBuilderPage;
